import {
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { addOutline, close } from 'ionicons/icons';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../api/authorization.provider';
import {
  EventChannel,
  EventChannelType,
  useListEventChannelsQuery,
} from '../../api/graphql/generated';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { EventIntegrationForm } from '../../components/event/event-integration-form';
import { NestedPageHeader } from '../../components/header/page.header';
import { FieldLocked } from '../../components/ui/field-locked';
import { useBeaconRouter } from '../../route/route.hooks';

const TenantEventIntegrationsPage = (): ReactElement => {
  const { i18n } = useTranslation();
  const { builder } = useBeaconRouter();

  const pageName = i18n.t('common:workspace_manage_integrations');

  const { infiniteScroll, items } = useListQueryWithInfiniteScroll({
    useQuery: useListEventChannelsQuery,
    queryOptions: {
      fetchPolicy: 'cache-and-network',
    },
  });

  const eventChannelItems = useMemo(
    () =>
      (items as EventChannel[]).filter((item) => {
        return item.type === EventChannelType.WEBHOOK;
      }),
    [items]
  );

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalIntegration, setModalIntegration] = useState<EventChannel>();

  const setModalState = (
    modalState: 'create' | 'update' | 'close',
    integration?: EventChannel
  ) => {
    if (modalState === 'create') {
      setModalIntegration(undefined);
      setModalVisible(true);
    } else if (modalState === 'update') {
      setModalIntegration(integration);
      setModalVisible(true);
    } else if (modalState === 'close') {
      setModalVisible(false);
    }
  };

  return (
    <IonPage>
      <NestedPageHeader
        title={pageName}
        defaultBackRoute={builder.getAdminRoute()}
      />
      <IonContent>
        <IonList lines="full">
          {eventChannelItems.map((item) => {
            return (
              <IntegrationItem
                key={item.id}
                eventChannel={item}
                onSelected={(integration) => {
                  setModalState('update', integration);
                }}
              />
            );
          })}
        </IonList>
        {infiniteScroll}
        <IonModal
          isOpen={modalVisible}
          onDidDismiss={() => {
            setModalState('close');
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot={'start'} onClick={() => setModalState('close')}>
                <IonButton>
                  <IonIcon icon={close} size="large" />
                </IonButton>
              </IonButtons>
              <IonTitle>{i18n.t('common:event_integration_details')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <EventIntegrationForm
            integration={modalIntegration}
            onUpdate={(integration) => {
              setModalIntegration(integration);
            }}
          />
        </IonModal>
        <IonFab
          vertical="bottom"
          horizontal="end"
          slot="fixed"
          className="ion-padding-bottom"
        >
          <IonFabButton onClick={() => setModalState('create')}>
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>
      </IonContent>
    </IonPage>
  );
};

interface IntegrationItemProps {
  eventChannel: EventChannel;
  onSelected: (integration: EventChannel) => void;
}

const IntegrationItem = (props: IntegrationItemProps): ReactElement => {
  const { eventChannel, onSelected } = props;
  const { i18n } = useTranslation();
  const handleClick = () => {
    onSelected(eventChannel);
  };

  const canUpdate = useHasPermission('update', 'event_channel', eventChannel);

  return (
    <IonItem onClick={handleClick} button={true}>
      {eventChannel.label} <FieldLocked show={!canUpdate} />
      {!eventChannel.active && (
        <IonLabel slot="end" color="medium">
          {i18n.t('common:inactive')}
        </IonLabel>
      )}
    </IonItem>
  );
};

export { TenantEventIntegrationsPage };
